import { useState, useEffect } from 'react'
import { useTrackerDetails } from 'api/trackers/queries/useTrackerDetails'
import { useParams } from 'react-router-dom'
import { trackerURLParams } from 'utils/routing/routes/utils'
import { useInstalledDataSystems } from 'api/dataSystems/queries/useInstalledDataSystems'
import { useDebounce } from 'react-use'
import { useApplications } from 'api/applications/queries/useApplications'
import { useOrganizationLanguages } from 'api/languages/queries/useOrganizationLanguages'
import { useUpdateTrackerDisclosure } from 'api/trackers/mutations/useUpdateTrackerDisclosure'
import { showToast } from 'components/ui-kit/toastr/Toastr'
import { ApiQueryKeys } from 'api/common/queryKeys'
import { useQueryClient } from 'react-query'
import { UpdateTrackerDisclosureBody } from '@ketch-com/supercargo/dist/tracker_gen.schemas'

export const useTrackerDetailsUtils = () => {
  const queryClient = useQueryClient()
  const { encodedTrackerKey } = useParams<trackerURLParams>()

  const { mutateAsync: updateTrackerDisclosure, isLoading: isUpdatingTrackerDisclosure } = useUpdateTrackerDisclosure({
    onSuccess: async () => {
      await queryClient.invalidateQueries([ApiQueryKeys.trackerDetails])
      showToast({ content: 'Updated tracker disclosure', type: 'success' })
    },
    onError: () => {
      showToast({ content: 'Failed to update tracker disclosure', type: 'error' })
    },
  })

  const { isLoading: isLoadingTrackerDetails, data: trackerDetails } = useTrackerDetails({
    params: {
      encodedTrackerKey,
    },
  })

  const [dataSystemInputValue, setDataSystemInputValue] = useState('')
  const [dataSystemSearchQueryValue, setDataSystemSearchQueryValue] = useState('')
  useDebounce(
    () => {
      if (dataSystemInputValue) {
        setDataSystemSearchQueryValue(dataSystemInputValue)
      } else {
        setDataSystemSearchQueryValue('')
      }
    },
    500,
    [dataSystemInputValue],
  )
  const { data: dataSystems, isLoading: isLoadingDataSystems } = useInstalledDataSystems({
    itemsPerPage: 300,
    params: {
      q: dataSystemSearchQueryValue,
    },
  })
  const { data: properties, isLoading: isLoadingProperties } = useApplications()

  const { data: orgLanguages, isLoading: isOrgLanguagesLoading } = useOrganizationLanguages()

  const [currentTab, setCurrentTab] = useState(0)

  const [disclosure, setDisclosure] = useState<UpdateTrackerDisclosureBody>({})

  useEffect(() => {
    if (trackerDetails?.disclosure) {
      setDisclosure(trackerDetails.disclosure)
    }
  }, [trackerDetails])

  const isReady = !isLoadingTrackerDetails && !isLoadingDataSystems && !isLoadingProperties && !isOrgLanguagesLoading

  return {
    isReady,
    trackerDetails,
    currentTab,
    setCurrentTab,
    encodedTrackerKey,
    dataSystems,
    isLoadingDataSystems,
    setDataSystemInputValue,
    dataSystemInputValue,
    properties,
    orgLanguages,
    updateTrackerDisclosure,
    isUpdatingTrackerDisclosure,
    disclosure,
    setDisclosure,
  }
}
